import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table";
import {Button} from "@/components/ui/button";
import {Trash, Edit} from "lucide-react";
import {Badge} from "@/components/ui/badge";
import {usePage} from "@inertiajs/react";
import {HistoryEntry} from "@/lib/types/models.ts";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card.tsx";

type Props = {
    entries: HistoryEntry[];
    onDelete: (entry: HistoryEntry) => void;
    onEdit?: (entry: HistoryEntry) => void;
};
export default function PatientHistoryTable({entries, onDelete, onEdit}: Props) {
    const {translations} = usePage().props as any;
    const t = translations.histories;

    if (entries.length === 0) {
        return (
            <div className="text-center py-6 text-muted-foreground">
                {t.no_history}
            </div>
        );
    }

    return (
        <>
            <div className="hidden md:block">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t.date}</TableHead>
                                <TableHead>{t.time}</TableHead>
                                <TableHead>{t.tooth}</TableHead>
                                <TableHead>{t.description}</TableHead>
                                <TableHead>{t.invoice}</TableHead>
                                <TableHead>{t.paid}</TableHead>
                                <TableHead>{t.actions}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {entries
                                .filter((e): e is HistoryEntry => !!e)
                                .map((e) => {
                                    const date = e.appointment_at ? new Date(e.appointment_at) : null;
                                    const formattedDate = date
                                        ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date)
                                        : "—";
                                    const formattedTime = date
                                        ? new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }).format(date)
                                        : "—";

                                    return (
                                        <TableRow key={e.id}>
                                            <TableCell>{formattedDate}</TableCell>
                                            <TableCell>{formattedTime}</TableCell>
                                            <TableCell>{e.tooth ?? "—"}</TableCell>
                                            <TableCell className="max-w-md">
                                                    <div
                                                        className="line-clamp-1 whitespace-pre-wrap break-words"
                                                        title={e.work_done ?? "—"}
                                                    >
                                                        {e.work_done
                                                            ? e.work_done.length > 85
                                                                ? e.work_done.slice(0, 85) + "…"
                                                                : e.work_done
                                                            : "—"}
                                                    </div>
                                                </TableCell>
                                            <TableCell>{e.bill != null ? `${Number(e.bill).toLocaleString()} din` : "—"}</TableCell>
                                            <TableCell>
                                                <Badge variant={e.paid ? "default" : "secondary"}>{e.paid ? t.paid : t.unpaid}</Badge>
                                            </TableCell>
                                            <TableCell className="flex gap-1">
                                                {onEdit && (
                                                    <Button variant="outline" size="icon" onClick={() => onEdit(e)}>
                                                        <Edit />
                                                    </Button>
                                                )}
                                                <Button variant="destructive" size="icon" onClick={() => onDelete(e)}>
                                                    <Trash />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <div className="block md:hidden space-y-3">
                {entries
                    .filter((e): e is HistoryEntry => !!e)
                    .map((e) => {
                        const date = e.appointment_at ? new Date(e.appointment_at) : null;
                        const formattedDate = date
                            ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date)
                            : "—";
                        const formattedTime = date
                            ? new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }).format(date)
                            : "—";

                        return (
                            <Card key={e.id} className="w-full max-w-full">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base">
                                        {formattedDate} · {formattedTime}
                                    </CardTitle>
                                </CardHeader>

                                <CardContent className="space-y-4 text-sm">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <div className="text-muted-foreground text-xs">{t.tooth}</div>
                                            <div className="truncate">{e.tooth ?? "—"}</div>
                                        </div>
                                        <div>
                                            <div className="text-muted-foreground text-xs">{t.invoice}</div>
                                            <div>{e.bill != null ? `${Number(e.bill).toLocaleString()} din` : "—"}</div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-muted-foreground text-xs">{t.description}</div>
                                        <div className="whitespace-pre-wrap">{e.work_done ?? "—"}</div>
                                    </div>

                                    <div className="flex items-center justify-between pt-2">
                                        <Badge variant={e.paid ? "default" : "secondary"}>
                                            {e.paid ? t.paid : t.unpaid}
                                        </Badge>

                                        <div className="flex gap-3">
                                            {onEdit && (
                                                <Button variant="outline" size="icon" onClick={() => onEdit(e)}>
                                                    <Edit />
                                                </Button>
                                            )}
                                            <Button variant="destructive" size="icon" onClick={() => onDelete(e)}>
                                                <Trash />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
            </div>
        </>
    );
}